import { fabric } from 'fabric'
import { modes } from './toolFactory'

// Define stamp objects with their SVG paths and default settings
export const stampObjects = {
  'Small Tree': {
    path: 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4Z',
    fill: '#2E7D32',
    scale: 2
  },
  'Large Tree': {
    path: 'M12,2L4,12H6V19H18V12H20L12,2M12,4.5L16.5,10H15V17H9V10H7.5L12,4.5Z',
    fill: '#1B5E20',
    scale: 3
  },
  'Palm Tree': {
    path: 'M12,15L7,20H11V24H13V20H17L12,15M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4Z',
    fill: '#33691E',
    scale: 2.5
  },
  'Bush': {
    path: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z',
    fill: '#388E3C',
    scale: 1.5
  },
  'Shrub': {
    path: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4Z',
    fill: '#558B2F',
    scale: 1.8
  },
  'Bench': {
    path: 'M4,2H20V8H4V2M6,4V6H18V4H6M4,10H20V16H4V10M6,12V14H18V12H6M4,18H20V24H4V18M6,20V22H18V20H6Z',
    fill: '#795548',
    scale: 2
  },
  'Table': {
    path: 'M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z',
    fill: '#8D6E63',
    scale: 2.5
  },
  'Chair': {
    path: 'M15,12V16H9V12H15M19,8H5V20H19V8M19,4H5A2,2 0 0,0 3,6V20A2,2 0 0,0 5,22H19A2,2 0 0,0 21,20V6A2,2 0 0,0 19,4M7,10H17V6H7V10Z',
    fill: '#8D6E63',
    scale: 1.8
  },
  'Umbrella': {
    path: 'M12,2A9,9 0 0,0 3,11H21A9,9 0 0,0 12,2M12,4A7,7 0 0,1 19,11H5A7,7 0 0,1 12,4M11,13V19H13V13H11Z',
    fill: '#F57C00',
    scale: 2.2
  },
  'Planter': {
    path: 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6Z',
    fill: '#A1887F',
    scale: 2
  }
}

export class StampTool {
  constructor(board) {
    this.board = board
    this.canvas = board.canvas
    this.currentStamp = null
    this.isActive = false
    this.mouseDown = false
    this.lastClickTime = 0
    this.lastStampTime = 0 // Track last stamp placement time
    this.doubleClickDelay = 300 // ms
    this.stampPositions = new Set() // Track recent positions to prevent duplicates
    this.placementDelay = 200 // Reasonable delay between placements (lowered from 500)
  }

  activate() {
    this.isActive = true
    this.canvas.defaultCursor = 'crosshair'
    this.canvas.selection = false
    this.attachEvents()
  }

  deactivate() {
    this.isActive = false
    this.detachEvents()
    this.currentStamp = null
  }

  setCurrentStamp(stampName, customStamp = null) {
    // Important debug logs
    console.log('setCurrentStamp called with:', { stampName, customStamp })
    
    // First check if it's a custom stamp by name before processing
    const savedStamps = localStorage.getItem('customStamps')
    const customStampsMap = savedStamps ? JSON.parse(savedStamps) : {}
    
    if (customStamp) {
      // For custom stamps, preserve the entire object
      this.currentStamp = { ...customStamp }
      
      // Store the name on the object
      this.currentStamp.name = stampName
      this.currentStamp.isCustom = true
      
      // Make sure all required properties are set
      if (!this.currentStamp.path && this.currentStamp.shape) {
        // Generate path if missing
        if (this.currentStamp.shape === 'circle') {
          this.currentStamp.path = 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z'
        } else {
          this.currentStamp.path = 'M4,4H20V20H4V4Z' // Simple rectangle path
        }
      }
      
      // Cache in memory to prevent loss on second call
      this._customStampCache = this.currentStamp
      
      // Display in console for debugging
      console.log('Custom stamp set:', this.currentStamp)
      return // Don't proceed further
    } else if (customStampsMap[stampName]) {
      // If it's a custom stamp name, use the saved custom stamp
      console.log('Found custom stamp by name in localStorage:', stampName)
      this.currentStamp = { ...customStampsMap[stampName] }
      this.currentStamp.isCustom = true
      this._customStampCache = this.currentStamp
      return
    } else if (this._customStampCache && this._customStampCache.name === stampName) {
      // Use cached version if available
      console.log('Using cached custom stamp:', stampName)
      this.currentStamp = this._customStampCache
      return
    } else {
      // For built-in stamps
      if (stampObjects[stampName]) {
        this.currentStamp = { ...stampObjects[stampName], name: stampName }
        console.log('Built-in stamp set:', this.currentStamp)
      } else {
        console.error(`Stamp ${stampName} not found in built-in stamps!`)
        this.currentStamp = null
      }
    }
  }
  
  getCurrentStamp() {
    return this.currentStamp
  }
  
  // Helper to get all available stamp objects
  getStampObjects() {
    return stampObjects
  }

  attachEvents() {
    this.canvas.on('mouse:down', this.onMouseDown)
    this.canvas.on('mouse:move', this.onMouseMove)
    this.canvas.on('mouse:up', this.onMouseUp)
  }

  detachEvents() {
    this.canvas.off('mouse:down', this.onMouseDown)
    this.canvas.off('mouse:move', this.onMouseMove)
    this.canvas.off('mouse:up', this.onMouseUp)
  }

  // Track when stamps are placed to prevent duplicates
  stampPlacedAt = { x: null, y: null, time: 0 }
  
  // Registry for recent stamp placements to properly prevent duplicates
  recentPlacements = []
  deduplicationTimeWindow = 2000 // ms - longer window to catch all duplicates
  deduplicationDistance = 5 // pixels - threshold for considering positions as duplicates
  
  onMouseDown = (opt) => {
    if (!this.isActive || !this.currentStamp) return
    
    const now = Date.now()
    // Only retain double-click prevention
    if (now - this.lastClickTime < this.doubleClickDelay) {
      // Double click detected, ignore
      console.log('Double click detected, ignoring')
      return
    }
    
    this.lastClickTime = now
    
    // Set mouseDown flag for drag operations
    this.mouseDown = true
    
    // Get current pointer position
    const pointer = this.canvas.getPointer(opt.e)
    
    // Important: Check for duplicates before placement
    if (this.isDuplicatePlacement(pointer)) {
      console.log('Duplicate detected in onMouseDown, skipping placement')
      return
    }
    
    // Place the stamp at the current position
    this.placeStamp(pointer)
    
    // Record where we just placed a stamp to prevent duplicates
    this.stampPlacedAt = {
      x: pointer.x,
      y: pointer.y,
      time: now
    }
  }

  onMouseMove = (opt) => {
    if (!this.isActive || !this.mouseDown || !this.currentStamp) return
    
    // Get the pointer position
    const pointer = this.canvas.getPointer(opt.e)
    
    // Calculate distance moved from last placement
    const dx = pointer.x - this.stampPlacedAt.x
    const dy = pointer.y - this.stampPlacedAt.y
    const distance = Math.sqrt(dx * dx + dy * dy)
    
    // Only place a new stamp if we've moved at least 20 pixels
    // AND it's been at least 100ms since the last placement
    const now = Date.now()
    const timeSinceLastPlacement = now - this.stampPlacedAt.time
    
    if (distance > 20 && timeSinceLastPlacement > 100) {
      this.placeStamp(pointer)
      
      // Update the last placement position and time
      this.stampPlacedAt = {
        x: pointer.x,
        y: pointer.y,
        time: now
      }
    }
  }

  onMouseUp = () => {
    this.mouseDown = false
  }

  // Older duplicate detection method, now replaced by isDuplicatePlacement
  // (Keeping the method name for compatibility with any existing calls, but it now calls the new method)
  isDuplicate(pointer) {
    return this.isDuplicatePlacement(pointer)
  }

  // Check if a placement would be a duplicate
  isDuplicatePlacement(pointer) {
    const now = Date.now()
    
    // Clean up old placements beyond our deduplication window
    this.recentPlacements = this.recentPlacements.filter(placement => {
      return now - placement.time < this.deduplicationTimeWindow
    })
    
    // Check if we have a recent placement at almost the same position
    for (const placement of this.recentPlacements) {
      const dx = pointer.x - placement.x
      const dy = pointer.y - placement.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      
      if (distance < this.deduplicationDistance) {
        console.log(`Preventing duplicate stamp at (${Math.round(pointer.x)}, ${Math.round(pointer.y)})`, 
                    `near existing stamp at (${Math.round(placement.x)}, ${Math.round(placement.y)})`)
        return true
      }
    }
    
    return false
  }

  placeStamp(pointer) {
    if (!this.currentStamp) {
      console.log('No current stamp selected')
      return
    }
    
    // Stop duplicate placements
    if (this.isDuplicatePlacement(pointer)) {
      return
    }
    
    // Log the position for debugging
    console.log(`Placing stamp at position (${Math.round(pointer.x)}, ${Math.round(pointer.y)})`)
    
    // For debugging
    console.log('Placing stamp:', this.currentStamp.name || this.currentStamp.text)
    
    // Register this position to prevent duplicates
    this.recentPlacements.push({
      x: pointer.x,
      y: pointer.y,
      time: Date.now(),
      id: Date.now() + '_' + Math.random().toString(36).substring(2, 9)
    })
    
    // Size map for different plant sizes
    const sizeMap = {
      'tiny': { radius: 20, fontSize: 14 },  // Perennials
      'small': { radius: 25, fontSize: 15 },  // Flowers
      'medium': { radius: 30, fontSize: 16 }, // Shrubs
      'large': { radius: 40, fontSize: 18 }   // Trees
    }
    
    // Get size values (default to medium if not specified)
    const size = this.currentStamp.size || 'medium'
    const sizeValues = sizeMap[size] || sizeMap.medium
    
    // Handle both custom and regular stamps
    if (this.currentStamp.isCustom) {
      // Create a consistent, properly marked shape for counting
      let stampObject;
      
      if (this.currentStamp.shape === 'circle') {
        stampObject = new fabric.Circle({
          radius: sizeValues.radius,
          fill: this.currentStamp.fill || '#4CAF50',
          stroke: '#000000',
          strokeWidth: 1,
          left: pointer.x,
          top: pointer.y,
          originX: 'center',
          originY: 'center',
          selectable: true,
          evented: true,
          // CRITICAL: Make sure these properties match what the ObjectCalculator expects
          objectType: 'stamp',
          stamp: true,
          stampName: this.currentStamp.name,
          isCustom: true,
          customStamp: true,
          size: size, // Store the size category
          opacity: 0.5  // 50% transparent fill
        });
      } else {
        // For rectangles, use twice the radius as width/height
        const rectSize = sizeValues.radius * 2;
        stampObject = new fabric.Rect({
          width: rectSize,
          height: rectSize,
          fill: this.currentStamp.fill || '#4CAF50',
          stroke: '#000000',
          strokeWidth: 1,
          left: pointer.x,
          top: pointer.y,
          originX: 'center',
          originY: 'center',
          selectable: true,
          evented: true,
          // CRITICAL: Make sure these properties match what the ObjectCalculator expects
          objectType: 'stamp',
          stamp: true,
          stampName: this.currentStamp.name,
          isCustom: true,
          customStamp: true,
          size: size, // Store the size category
          opacity: 0.5  // 50% transparent fill
        });
      }
      
      // Text for the stamp
      const textContent = this.currentStamp.text || this.currentStamp.symbol || 'ABC';
      const text = new fabric.Text(textContent, {
        left: 0, // Center of group
        top: 0, // Center of group
        fontSize: sizeValues.fontSize, // Use size-appropriate font size
        fill: '#000000',   // Simple black text
        fontFamily: 'Arial, sans-serif', // Regular font
        fontWeight: 'normal',
        textAlign: 'center',
        originX: 'center',
        originY: 'center',
        objectType: 'text', // Not a stamp for counting purposes
        opacity: 1         // Keep text fully opaque for readability
      });
      
      // Reset position for grouping
      stampObject.set({
        left: 0,
        top: 0
      });
      
      // Create a group containing both the shape and text
      const group = new fabric.Group([stampObject, text], {
        left: pointer.x,
        top: pointer.y,
        originX: 'center',
        originY: 'center',
        selectable: true,
        evented: true,
        // Maintain the original stamp properties on the group for proper counting
        objectType: 'stamp',
        stamp: true,
        stampName: this.currentStamp.name,
        isCustom: true,
        customStamp: true,
        size: size
      });
      
      // Add the grouped object to the canvas
      this.canvas.add(group);
      console.log('Added grouped custom stamp:', group);
      
      // Force a full render
      this.canvas.renderAll();
    } else {
      // For regular stamps, use the standard path approach
      const stampObject = new fabric.Path(this.currentStamp.path, {
        left: pointer.x,
        top: pointer.y,
        fill: this.currentStamp.fill,
        stroke: 'rgba(0,0,0,0.2)',
        strokeWidth: 1,
        scaleX: this.currentStamp.scale,
        scaleY: this.currentStamp.scale,
        originX: 'center',
        originY: 'center',
        selectable: true,
        evented: true,
        // CRITICAL: Make sure these properties match what the ObjectCalculator expects
        stamp: true,
        objectType: 'stamp',
        stampName: this.currentStamp.name
      });
      
      // Add the stamp to canvas
      this.canvas.add(stampObject);
      console.log('Added regular stamp:', this.currentStamp.name);
    }
    
    // Take a snapshot for history
    if (this.board.history) {
      this.board.history.snapshot();
    }
  }
  
  // Helper method to get contrasting text color for better visibility
  getContrastingColor(hexColor) {
    // Default to white if no color provided
    if (!hexColor) return '#FFFFFF'
    
    // Convert hex to RGB
    let hex = hexColor.replace('#', '')
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    }
    
    // Parse the color components
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    
    // Calculate relative luminance using common formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    
    // Return white for dark colors, black for light colors
    return luminance > 0.5 ? '#000000' : '#FFFFFF'
  }
}

// StampObjects are now directly exported at the top of the file
